import { Box } from "@mui/material";
import { darken } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { ValueByClaimType } from "../../shared/PortfolioTypes.ts";
import { ClaimTypeRow, fillOf, lossFill } from "./ClaimTypeBreakdown";
import { perCaseRows } from "./perCase";

/**
 * The mark of an average reaches this far beyond the track on either side,
 * in px, and is this wide; it is darkened this far from the colour of its
 * range.
 */
const MARK_OVERHANG = 4;
const MARK_WIDTH = 3;
const MARK_DARKEN = 0.45;

/** The line of nothing reaches this far beyond the track on either side, in px. */
const ZERO_OVERHANG = 2;

/**
 * What a case brings in each set of claim types, a row per set as the list of
 * the totals has them: the average as the amount, and on the track the range
 * from the lowest case to the highest marked at the average, the two ends of
 * it in the tooltip of the row, or the average alone where a single case is
 * its own lowest and highest. The highest average comes first, with what a
 * case brings over all the cases among them; one scale holds every row.
 */
export const ClaimTypeAverages = ({
  values,
}: {
  values: ValueByClaimType[];
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, { keyPrefix: "ClaimTypeAverages" });

  const rows = perCaseRows(values);
  // from nothing, or from the lowest loss, to the highest case
  const start = Math.min(0, ...rows.map(({ lowest }) => lowest));
  const end = Math.max(0, ...rows.map(({ highest }) => highest));
  const at = (amount: number) =>
    end > start ? ((amount - start) / (end - start)) * 100 : 0;

  return (
    <div>
      {rows.map((row) => {
        // a loss is told by its colour, whatever the claim types
        const isLoss = row.average < 0;
        const fill = isLoss ? lossFill(theme) : fillOf(row, theme);
        const mark = isLoss
          ? theme.palette.data.negative.dark
          : darken(fill, MARK_DARKEN);
        const from = at(row.lowest);
        const to = at(row.highest);
        // a single case is its own lowest and highest
        const hasRange = row.size > 1;

        return (
          <ClaimTypeRow
            key={row.kind === "set" ? row.claimTypes.join() : row.kind}
            group={row}
            size={row.size}
            amount={row.average}
            track={
              <>
                {/* where a case is a loss, the line of nothing tells it */}
                {start < 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -ZERO_OVERHANG,
                      bottom: -ZERO_OVERHANG,
                      left: `${at(0)}%`,
                      width: "1px",
                      bgcolor: "text.disabled",
                    }}
                  />
                )}
                {hasRange && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: `${from}%`,
                      width: `${to - from}%`,
                      borderRadius: 999,
                      bgcolor: fill,
                    }}
                  />
                )}
                <Box
                  sx={{
                    position: "absolute",
                    top: -MARK_OVERHANG,
                    bottom: -MARK_OVERHANG,
                    // the whole mark stays on the track at either end of it
                    left: `clamp(0px, calc(${at(row.average)}% - ${MARK_WIDTH / 2}px), calc(100% - ${MARK_WIDTH}px))`,
                    width: MARK_WIDTH,
                    borderRadius: 0.25,
                    bgcolor: mark,
                  }}
                />
              </>
            }
            // the tooltip points at the mark of the average
            tooltip={{
              text: hasRange
                ? t("range", {
                    lowest: currency.format(row.lowest),
                    highest: currency.format(row.highest),
                  })
                : t("average", { average: currency.format(row.average) }),
              at: at(row.average),
            }}
          />
        );
      })}
    </div>
  );
};
